import React from 'react'
import { GatsbyImage, getImage, StaticImage } from 'gatsby-plugin-image'
import { CallToAction } from 'components/CallToAction'
import { Avatar } from './Avatar'
import { graphql, useStaticQuery } from 'gatsby'
import Map from './Map'

interface DotProps {
    classes: string
}

const Dot = ({ classes }: DotProps) => {
    return (
        <div className={`absolute ${classes}`}>
            <span className="relative mx-auto inline-flex h-4 w-4 rounded-full border-[2.5px] border-solid border-white bg-red">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red opacity-75"></span>
            </span>
        </div>
    )
}

const avatarStyles = [
    { color: '#DCB1E3', className: 'right-[-30.5rem] top-[-2.5rem]', size: 'lg' },
    { color: '#FDEDC9', className: 'right-[-26rem] top-[-12rem]', size: 'md' },
    { color: '#E19D9D', className: 'right-[-14.5rem] bottom-[-16rem]', size: 'xl' },
    { color: '#A2B0D4', className: 'right-[-14rem] top-[-22.5rem]', size: 'lg' },
    { color: '#9DE1D9', className: 'left-[-30.5rem] top-[-18.5rem]', size: 'xl' },
    { color: '#B3E19D', className: 'left-[-10rem] top-[-19rem]', size: 'md' },
    { color: '#9DA4E1', className: 'left-[-23rem] bottom-[-14rem]', size: 'xl' },
    { color: '#FBBC05', className: 'left-[-25rem] bottom-[-1.5rem]', size: 'md' },
    { color: '#E6A9E8', className: 'left-[-6rem] bottom-[-12rem]', size: 'md' },
    { color: '#E6A9E8', className: 'left-[-6rem] bottom-[-12rem]', size: 'md' },
]

const AboutTeamMapSubSection = ({ avatarStyles, locationsData, teamMembers, teamMembersToPresent }) => {
    return (
        <div className="relative py-14 text-center md:py-28">
            <div className="absolute inset-1/2 scale-[.4] sm:scale-[.6] md:scale-100">
                {teamMembers?.nodes
                    ?.filter((teamMember) => {
                        const teamMemberToBePresented = teamMembersToPresent.includes(teamMember.frontmatter.name)
                        // console.log(`teamMemberToBePresented ${JSON.stringify(teamMemberToBePresented)}`)
                        return teamMemberToBePresented && teamMembersToPresent.length > 0
                    })
                    .map(({ frontmatter: { name, country, headshot }, parent }, index) => {
                        const styles = avatarStyles[index]
                        const headshotImage = headshot || parent?.cover?.image
                        return (
                            <Avatar
                                key={name}
                                size={styles.size}
                                className={styles.className}
                                color={styles.color}
                                image={headshotImage}
                                name={name}
                                country={country}
                            />
                        )
                    })}
            </div>
            <div className="mx-auto w-full max-w-[300px] sm:max-w-[400px] md:max-w-[700px]">
                <Map locations={locationsData} />
            </div>
        </div>
    )
}
//
const AboutTeamTitleSubSection = ({ image, title, cta }) => {
    return (
        <>
            <h3 className="mb-4 text-center text-5xl lg:mb-1">{title?.title}</h3>
            <h4 className="text-center font-semibold opacity-70">{title?.subtitle}</h4>
            <div className="mb-4 text-center">
                <CallToAction to={cta.url} type="secondary">
                    {cta.name}
                </CallToAction>
            </div>
        </>
    )
}

export const AboutTeam = ({ allAboutTeam, googleImages }) => {
    const { aboutTeamTitle, image, id, style, teamMembersToPresent, locations, enable } = allAboutTeam
    if ((enable === false || enable?.toLowerCase?.() === 'false') && !process.env.RENDER_DISABLED_COMPONENTS) {
        return <></>
    }
    let cta = allAboutTeam?.cta
    if (Array.isArray(cta) && cta.length == 1) cta = cta[0]
    const googleImage =
        image &&
        googleImages &&
        googleImages.filter((img) => {
            if (img.name == image) {
                return img
            }
        })[0]
    const gatsbyImage = googleImage && getImage(googleImage)
    const { teamMembers } = useStaticQuery(query)
    return (
        <section id={id} className={`px-4 pt-16 pb-12 ${style}`}>
            <AboutTeamTitleSubSection image={gatsbyImage} title={aboutTeamTitle} cta={cta} />
            <AboutTeamMapSubSection
                image={gatsbyImage}
                avatarStyles={avatarStyles}
                teamMembers={teamMembers}
                locationsData={locations}
                teamMembersToPresent={teamMembersToPresent}
            />
        </section>
    )
}

const query = graphql`
    {
        teamMembers: allMdx(
            filter: { fields: { slug: { regex: "/^/team//" } } }
            sort: { fields: frontmatter___startDate }
        ) {
            nodes {
                frontmatter {
                    country
                    name
                    headshot {
                        childImageSharp {
                            gatsbyImageData(placeholder: NONE)
                        }
                    }
                }
                parent {
                    ... on GoogleDocs {
                        cover {
                            image {
                                childImageSharp {
                                    gatsbyImageData
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`
